import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { AI_SWITCH_ON_CLASS } from '@common-components/ai-chat/parts';
import { SettingsPickerBlock, SettingKey, BtgptPermissions } from '../types/btgpt-types';

/** Friendly display label for a raw option id. */
export function settingOptionLabel(setting: SettingKey, option: string): string {
  if (setting === 'permissions') {
    if (option === 'allow-all') return 'Allow all';
    if (option === 'ask-before-sending') return 'Ask before sending';
  }
  return option.charAt(0).toUpperCase() + option.slice(1);
}

/** The chat command that also drives this setting — shown as a hint. */
const SETTING_COMMAND: Record<SettingKey, string> = {
  model: '/model',
  permissions: '/permissions',
};

const SETTING_TITLE: Record<SettingKey, string> = {
  model: 'Model',
  permissions: 'Permissions',
};

interface Props {
  block: SettingsPickerBlock;
  /** Apply a chosen option (PATCH + header sync handled by the parent). */
  onChoose: (setting: SettingKey, option: string) => void;
}

/**
 * Inline picker rendered under an assistant message when the user runs a bare
 * `/model` or `/permissions`. Shows the current value + a button per
 * option; clicking one applies it. Once applied, the block shows the resolved
 * choice and disables further clicks.
 */
export const SettingsPickerBlockView: React.FC<Props> = ({ block, onChoose }) => {
  const { setting, current, options, chosen } = block;
  return (
    <div className="mt-1 rounded-ai-md border border-ai-border bg-ai-surface p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-ai-text-secondary">
          {SETTING_TITLE[setting]} · currently <span className="font-semibold text-ai-text">{current}</span>
        </span>
        <span className="text-[10px] text-ai-text-faint font-mono">{SETTING_COMMAND[setting]}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isChosen = chosen === option;
          const isCurrent = !chosen && settingOptionLabel(setting, option).toLowerCase() === current.toLowerCase();
          const active = isChosen || isCurrent;
          return (
            <button
              key={option}
              type="button"
              disabled={!!chosen}
              onClick={() => onChoose(setting, option)}
              className={[
                'px-3 py-1 rounded-full text-xs font-medium transition-colors border',
                active
                  ? 'bg-ai-text text-ai-bg border-ai-text'
                  : 'bg-ai-surface text-ai-text border-ai-border hover:border-ai-border-strong',
                chosen ? 'opacity-70 cursor-default' : 'cursor-pointer',
              ].join(' ')}
            >
              {settingOptionLabel(setting, option)}
              {isChosen ? ' ✓' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface PermissionsPickerProps {
  /** Live permissions state (not a snapshot) so every copy of the toggles stays in sync. */
  permissions: BtgptPermissions;
  onToggle: (key: keyof BtgptPermissions, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  /** Show the one-line explanation under each toggle (the chat menu does; the inline card does not). */
  showHints?: boolean;
  /** Extra rows under the four capabilities (the chat menu adds files auto-recognition). */
  children?: React.ReactNode;
  /** No card frame (inside a menu). */
  bare?: boolean;
}

/**
 * The permission toggles (Allow all + send / schedule / history / archive) —
 * ONE list, used by the `/permissions` inline card and by the chat `⋯` menu,
 * wired to the same handlers + live state wherever it shows.
 */
export const PermissionsPickerCard: React.FC<PermissionsPickerProps> = ({
  permissions,
  onToggle,
  onToggleAll,
  showHints,
  children,
  bare,
}) => {
  const { t } = useTranslation();
  const allOn = permissions.send && permissions.schedule && permissions.history && permissions.archive;
  const rows: { key: keyof BtgptPermissions; label: string; hint: string }[] = [
    {
      key: 'send',
      label: t('btgpt.permissions.send', 'Send messages'),
      hint: t(
        'btgpt.permissions.sendHint',
        'On: the agent sends WhatsApp messages on its own. Off: it asks you to approve the recipient and content first.'
      ),
    },
    {
      key: 'schedule',
      label: t('btgpt.permissions.schedule', 'Schedule messages'),
      hint: t(
        'btgpt.permissions.scheduleHint',
        'On: the agent schedules messages on its own. Off: it asks you to approve before scheduling.'
      ),
    },
    {
      key: 'history',
      label: t('btgpt.permissions.history', 'Load full history'),
      hint: t(
        'btgpt.permissions.historyHint',
        'On: the agent pages as deep into chat history as needed. Off: it asks before pulling more than the first page.'
      ),
    },
    {
      key: 'archive',
      label: t('btgpt.permissions.archive', 'Archive / organize'),
      hint: t('btgpt.permissions.archiveHint', 'On: the agent archives, unarchives, or manages groups on its own. Off: it asks first.'),
    },
  ];
  return (
    <div className={bare ? '' : 'mt-1 rounded-ai-md border border-ai-border bg-ai-surface p-3'}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-ai-text">{t('btgpt.permissions.title', 'Agent permissions')}</span>
        <span className="text-[10px] text-ai-text-faint font-mono select-text cursor-text">/permissions</span>
      </div>
      <label className="flex items-center justify-between gap-3 pb-2 mb-1.5 border-b border-ai-border cursor-pointer">
        <span className="text-xs font-medium text-ai-text">{t('btgpt.permissions.allowAll', 'Allow all')}</span>
        <Switch
          className={AI_SWITCH_ON_CLASS}
          checked={allOn}
          onCheckedChange={onToggleAll}
          aria-label={t('btgpt.permissions.allowAll', 'Allow all')}
        />
      </label>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <label key={row.key} className="flex items-start justify-between gap-3 cursor-pointer">
            <span className="flex flex-col gap-0.5">
              <span className="text-xs text-ai-text">{row.label}</span>
              {showHints && <span className="text-[11px] leading-snug text-ai-text-secondary">{row.hint}</span>}
            </span>
            <Switch
              className={`mt-0.5 shrink-0 ${AI_SWITCH_ON_CLASS}`}
              checked={permissions[row.key]}
              onCheckedChange={(checked) => onToggle(row.key, checked)}
              aria-label={row.label}
            />
          </label>
        ))}
        {children}
      </div>
    </div>
  );
};
